import { getCurrentScope, onScopeDispose, reactive } from 'vue'

import api from '@/store/api'

export const TEST_POLL_MS = 5000

const QUIET = { receive: false, save: false }
const START_ERROR = 'The test text could not be sent. Try again.'

const idOrNull = value => (value === null || value === undefined ? null : String(value))

const startError = error => {
  const body = error?.response?.data ?? {}
  return { message: body.errors?.[0] ?? body.error ?? START_ERROR, reasonCode: body.reason_code ?? null }
}

// One agent's test conversation with the merchant's own number. While a test
// session is live it polls so replies from the merchant's phone show up.
export function useAgentTestConversation(agentId) {
  const path = `agents/${agentId}/test_conversation`
  const state = reactive({
    loading: false,
    eligible: false,
    reason: '',
    reasonCode: null,
    liveTestAgentId: null,
    session: null,
    error: ''
  })
  let timer = null
  let stopped = false

  const live = () => Boolean(state.session && !state.session.completed_at)

  const clearTimer = () => {
    clearTimeout(timer)
    timer = null
  }

  const apply = data => {
    state.eligible = Boolean(data?.eligible)
    state.reason = data?.reason ?? ''
    state.reasonCode = data?.reason_code ?? null
    state.liveTestAgentId = idOrNull(data?.live_test_agent_id)
    state.session = data?.session ?? null
  }

  const schedule = () => {
    clearTimer()
    if (!stopped && live()) timer = setTimeout(fetchState, TEST_POLL_MS)
  }

  // A failed read keeps the last state; a live session keeps polling.
  async function fetchState() {
    state.loading = true
    try {
      const response = await api.call('GET', path, {}, QUIET)
      apply(response.data?.data)
    } catch {
      // keep the last known state
    } finally {
      state.loading = false
      schedule()
    }
  }

  const refresh = () => {
    stopped = false
    return fetchState()
  }

  const start = async segment => {
    stopped = false
    state.error = ''
    try {
      await api.call('POST', path, segment ? { segment } : {}, QUIET)
    } catch (error) {
      const { message, reasonCode } = startError(error)
      state.error = message
      if (reasonCode) state.reasonCode = reasonCode
      return false
    }
    await fetchState()
    return true
  }

  const stop = () => {
    stopped = true
    clearTimer()
  }

  if (getCurrentScope()) onScopeDispose(stop)

  return { state, refresh, start, stop }
}
